import React, { useState, useMemo, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Slider } from "./ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { cn } from "../lib/utils";
import {
  DollarSign,
  Percent,
  AlertTriangle,
  CheckCircle,
  ShoppingCart,
  X,
  Minus,
  Plus,
  Calculator,
  Target,
} from "lucide-react";
import { toast } from "sonner";

/**
 * BulkPriceRequestModal - Modal for creating multiple price requests at once
 * 
 * Features:
 * - Services grouped by category
 * - Quantity controls with sliders
 * - Discount option with required justification
 * - Live summary with totals
 */
const BulkPriceRequestModal = ({
  open,
  onOpenChange,
  serviceTypes = [],
  basePrices = {},
  projectId,
  onSubmit,
  vexosFees = {
    base_fee: 1500,
    orbit_fee: 400,
    unit_fee_apartments: 25,
    unit_fee_villas: 100,
    fee_360: 100,
    custom_design: 1000,
    rush_deadline: 500,
    vexos_commission_percent: 5
  },
}) => {
  // Selected services state: { serviceTypeId: { quantity, discount, discountReason, discountAmount, discountMode } }
  // discountMode: 'percent' | 'fixed'
  const [selectedServices, setSelectedServices] = useState({});
  const [globalNotes, setGlobalNotes] = useState("");
  const [deadline, setDeadline] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [targetTotal, setTargetTotal] = useState(""); // Target total for auto-distribution
  
  // VEXOS Components state
  const [vexosData, setVexosData] = useState({
    base_fee_qty: 0,
    orbit_fee_qty: 0,
    unit_apartments_qty: 0,
    unit_villas_qty: 0,
    fee_360_qty: 0,
    orbit_animation: false, // $2500 first + $1000/extra
    custom_design: false,
    rush_deadline: false,
    client_invoice: 0,
  });
  const [vexosDiscount, setVexosDiscount] = useState({ percent: 0, fixed: 0, mode: 'percent' }); // VEXOS discount
  const [showVexos, setShowVexos] = useState(false);
  
  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedServices({});
      setGlobalNotes("");
      setDeadline("");
      setTargetTotal("");
      setVexosData({
        base_fee_qty: 0,
        orbit_fee_qty: 0,
        unit_apartments_qty: 0,
        unit_villas_qty: 0,
        fee_360_qty: 0,
        orbit_animation: false,
        custom_design: false,
        rush_deadline: false,
        client_invoice: 0,
      });
      setVexosDiscount({ percent: 0, fixed: 0, mode: 'percent' });
      setShowVexos(false);
    }
  }, [open]);
  
  // Calculate VEXOS total
  const calculateVexosTotal = useMemo(() => {
    const v = vexosData;
    const fees = vexosFees;
    
    // Orbit Animation Rendering: $2500 first + $1000/extra
    let orbitAnimationCost = 0;
    if (v.orbit_animation && v.orbit_fee_qty > 0) {
      orbitAnimationCost = 2500 + Math.max(0, v.orbit_fee_qty - 1) * 1000;
    }
    
    const componentsTotal = 
      (fees.base_fee * (v.base_fee_qty || 0)) +
      (fees.orbit_fee * (v.orbit_fee_qty || 0)) +
      (fees.unit_fee_apartments * (v.unit_apartments_qty || 0)) +
      (fees.unit_fee_villas * (v.unit_villas_qty || 0)) +
      (fees.fee_360 * (v.fee_360_qty || 0)) +
      orbitAnimationCost +
      (v.custom_design ? fees.custom_design : 0) +
      (v.rush_deadline ? fees.rush_deadline : 0);
    
    const commission = (v.client_invoice || 0) * (fees.vexos_commission_percent / 100);
    
    const subtotal = componentsTotal + commission;
    
    // Apply discount
    let discountAmount = 0;
    if (vexosDiscount.mode === 'percent' && vexosDiscount.percent > 0) {
      discountAmount = subtotal * (vexosDiscount.percent / 100);
    } else if (vexosDiscount.mode === 'fixed' && vexosDiscount.fixed > 0) {
      discountAmount = vexosDiscount.fixed;
    }
    
    const total = Math.max(0, subtotal - discountAmount);
    
    return {
      componentsTotal,
      commission,
      orbitAnimationCost,
      subtotal,
      discountAmount,
      total
    };
  }, [vexosData, vexosFees, vexosDiscount]);
  
  // Group services by category (first word or prefix before underscore)
  const groupedServices = useMemo(() => {
    const groups = {};
    
    // Define category mappings
    const getCategoryFromName = (name) => {
      const nameLower = name.toLowerCase();
      if (nameLower.includes("vr") || nameLower.includes("360") || nameLower.includes("virtual tour")) {
        return "VR Tours";
      }
      if (nameLower.includes("exterior") || nameLower.includes("interior") || nameLower.includes("aerial") || nameLower.includes("render")) {
        return "Rendering Services";
      }
      if (nameLower.includes("floor plan") || nameLower.includes("3d")) {
        return "3D Services";
      }
      if (nameLower.includes("animation") || nameLower.includes("video")) {
        return "Animation & Video";
      }
      if (nameLower.includes("model")) {
        return "Modeling";
      }
      return "Other Services";
    };
    
    serviceTypes.forEach(service => {
      if (service.is_active === false) return; // Skip inactive services
      
      const category = getCategoryFromName(service.name);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(service);
    });
    
    // Sort categories
    const orderedCategories = ["VR Tours", "Rendering Services", "3D Services", "Animation & Video", "Modeling", "Other Services"];
    const sortedGroups = {};
    orderedCategories.forEach(cat => {
      if (groups[cat] && groups[cat].length > 0) {
        sortedGroups[cat] = groups[cat];
      }
    });
    
    return sortedGroups;
  }, [serviceTypes]);
  
  // Calculate total summary
  const summary = useMemo(() => {
    const items = [];
    let total = 0;
    let totalWithoutDiscount = 0;
    
    Object.entries(selectedServices).forEach(([serviceId, data]) => {
      if (data.quantity > 0) {
        const service = serviceTypes.find(s => s.id === serviceId);
        if (!service) return;
        
        const basePrice = service.partner_price || basePrices[service.name] || 0;
        const originalPrice = basePrice * data.quantity;
        
        // Calculate discount based on mode
        let discountAmount = 0;
        let discountPercent = 0;
        
        if (data.discountMode === 'fixed' && data.discountAmount > 0) {
          // Fixed amount mode
          discountAmount = Math.min(data.discountAmount, originalPrice);
          discountPercent = originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;
        } else if (data.discount > 0) {
          // Percent mode
          discountPercent = data.discount;
          discountAmount = (originalPrice * data.discount / 100);
        }
        
        const finalPrice = originalPrice - discountAmount;
        
        items.push({
          id: serviceId,
          name: service.name,
          quantity: data.quantity,
          unitPrice: basePrice,
          originalPrice,
          discount: discountPercent,
          discountAmount,
          finalPrice,
          discountReason: data.discountReason || "",
          discountMode: data.discountMode || 'percent',
        });
        
        total += finalPrice;
        totalWithoutDiscount += originalPrice;
      }
    });
    
    return { items, total, totalWithoutDiscount, totalDiscount: totalWithoutDiscount - total };
  }, [selectedServices, serviceTypes, basePrices]);
  
  // Grand total including VEXOS
  const grandTotal = useMemo(() => {
    const vexosTotal = calculateVexosTotal.total;
    return {
      services: summary.total,
      vexos: vexosTotal,
      total: summary.total + vexosTotal,
      totalWithoutDiscount: summary.totalWithoutDiscount + vexosTotal,
    };
  }, [summary, calculateVexosTotal]);
  
  // Update service quantity
  const updateQuantity = (serviceId, quantity) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        quantity: Math.max(0, quantity),
      }
    }));
  };
  
  // Update discount for a service (percent mode)
  const updateDiscount = (serviceId, discount) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        discount: Math.max(0, Math.min(100, discount)),
        discountMode: 'percent',
        discountAmount: 0, // Reset fixed amount when switching to percent
      }
    }));
    setTargetTotal(""); // Clear target total when manually changing discount
  };
  
  // Update discount amount for a service (fixed amount mode)
  const updateDiscountAmount = (serviceId, amount) => {
    const service = serviceTypes.find(s => s.id === serviceId);
    const basePrice = service?.partner_price || basePrices[service?.name] || 0;
    const serviceData = selectedServices[serviceId] || {};
    const originalPrice = basePrice * (serviceData.quantity || 0);
    
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        discountAmount: Math.max(0, Math.min(amount, originalPrice)),
        discountMode: 'fixed',
        discount: 0, // Reset percent when switching to fixed
      }
    }));
    setTargetTotal(""); // Clear target total when manually changing discount
  };
  
  // Toggle discount mode between percent and fixed
  const toggleDiscountMode = (serviceId) => {
    const serviceData = selectedServices[serviceId] || {};
    const currentMode = serviceData.discountMode || 'percent';
    const newMode = currentMode === 'percent' ? 'fixed' : 'percent';
    
    // Convert current discount to new mode
    const service = serviceTypes.find(s => s.id === serviceId);
    const basePrice = service?.partner_price || basePrices[service?.name] || 0;
    const originalPrice = basePrice * (serviceData.quantity || 0);
    
    let newDiscount = 0;
    let newAmount = 0;
    
    if (newMode === 'fixed' && serviceData.discount > 0) {
      // Convert percent to amount
      newAmount = Math.round(originalPrice * serviceData.discount / 100);
    } else if (newMode === 'percent' && serviceData.discountAmount > 0) {
      // Convert amount to percent
      newDiscount = originalPrice > 0 ? Math.round((serviceData.discountAmount / originalPrice) * 100) : 0;
    }
    
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        discountMode: newMode,
        discount: newDiscount,
        discountAmount: newAmount,
      }
    }));
  };
  
  // Apply target total - distribute discount proportionally across all services
  const applyTargetTotal = (target) => {
    const targetValue = parseFloat(target);
    if (isNaN(targetValue) || targetValue < 0) return;
    
    // Calculate current subtotal (without discounts)
    const subtotal = summary.totalWithoutDiscount;
    if (subtotal === 0) return;
    
    // Target can't be more than subtotal
    if (targetValue >= subtotal) {
      // Clear all discounts
      setSelectedServices(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(serviceId => {
          if (updated[serviceId].quantity > 0) {
            updated[serviceId] = {
              ...updated[serviceId],
              discount: 0,
              discountAmount: 0,
              discountMode: 'percent',
            };
          }
        });
        return updated;
      });
      return;
    }
    
    // Calculate total discount needed
    const totalDiscountNeeded = subtotal - targetValue;
    
    // Distribute discount proportionally based on each service's share of the subtotal
    setSelectedServices(prev => {
      const updated = { ...prev };
      
      Object.keys(updated).forEach(serviceId => {
        const serviceData = updated[serviceId];
        if (serviceData.quantity > 0) {
          const service = serviceTypes.find(s => s.id === serviceId);
          const basePrice = service?.partner_price || basePrices[service?.name] || 0;
          const originalPrice = basePrice * serviceData.quantity;
          
          // Calculate this service's share of the total discount
          const sharePercent = originalPrice / subtotal;
          const serviceDiscount = totalDiscountNeeded * sharePercent;
          
          // Convert to percent for this service
          const discountPercent = originalPrice > 0 ? Math.round((serviceDiscount / originalPrice) * 100) : 0;
          
          updated[serviceId] = {
            ...serviceData,
            discount: Math.min(discountPercent, 100),
            discountAmount: 0,
            discountMode: 'percent',
          };
        }
      });
      
      return updated;
    });
  };
  
  // Update discount reason
  const updateDiscountReason = (serviceId, reason) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        discountReason: reason,
      }
    }));
  };
  
  // Remove item from cart
  const removeFromCart = (serviceId) => {
    setSelectedServices(prev => {
      const newState = { ...prev };
      delete newState[serviceId];
      return newState;
    });
  };
  
  // Validate before submit
  const validateSubmission = () => {
    // Check if any items selected
    if (summary.items.length === 0) {
      toast.error("Please select at least one service");
      return false;
    }
    
    // Check discount reasons - for both percent and fixed amount discounts
    for (const item of summary.items) {
      if ((item.discount > 0 || item.discountAmount > 0) && !item.discountReason.trim()) {
        toast.error(`Please provide a reason for the discount on "${item.name}"`);
        return false;
      }
    }
    
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateSubmission()) return;
    
    setSubmitting(true);
    try {
      // Create array of price requests
      const requests = summary.items.map(item => ({
        project_id: projectId,
        task_name: item.name,
        task_type: serviceTypes.find(s => s.id === item.id)?.name || item.name,
        service_type_id: item.id,
        proposed_price: item.finalPrice / item.quantity, // Price per unit after discount
        quantity: item.quantity,
        notes: (item.discount > 0 || item.discountAmount > 0)
          ? `Discount: ${item.discount}% ($${item.discountAmount?.toLocaleString() || Math.round(item.originalPrice * item.discount / 100)}) | Reason: ${item.discountReason}${globalNotes ? ` | Notes: ${globalNotes}` : ''}`
          : globalNotes,
        deadline: deadline || null,
        base_price: item.unitPrice,
        discount_percent: item.discount || 0,
        discount_amount: item.discountAmount || 0,
        discount_reason: item.discountReason || "",
      }));
      
      // Add VEXOS request if filled
      if (calculateVexosTotal.total > 0) {
        requests.push({
          project_id: projectId,
          task_name: "VEXOS Components",
          task_type: "VEXOS",
          proposed_price: calculateVexosTotal.total,
          quantity: 1,
          notes: globalNotes || "",
          deadline: deadline || null,
          base_price: calculateVexosTotal.total,
          discount_percent: 0,
          discount_amount: 0,
          discount_reason: "",
          is_vexos_task: true,
          vexos_data: vexosData,
        });
      }
      
      await onSubmit(requests);
      toast.success(`${requests.length} price request(s) submitted successfully!`);
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting price requests:", error);
      toast.error("Failed to submit price requests");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Get service price
  const getServicePrice = (service) => {
    return service.partner_price || basePrices[service.name] || 0;
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-800">
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-orange-400" />
            Create Price Requests
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Service Selection */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="bulk-request-services">
            {Object.entries(groupedServices).map(([category, services]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-semibold text-zinc-50 border-b border-zinc-800 pb-2">
                  {category}
                </h3>
                
                <div className="space-y-2">
                  {services.map(service => {
                    const price = getServicePrice(service);
                    const serviceData = selectedServices[service.id] || { quantity: 0, discount: 0, discountAmount: 0, discountMode: 'percent' };
                    const isSelected = serviceData.quantity > 0;
                    
                    return (
                      <div 
                        key={service.id}
                        className={cn(
                          "p-3 rounded-lg border transition-all",
                          isSelected 
                            ? "bg-orange-500/10 border-orange-500/30" 
                            : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                        )}
                        data-testid={`service-item-${service.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          {/* Service Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-zinc-50">{service.name}</span>
                              {service.is_sq_ft_pricing && (
                                <Badge className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                                  sq ft
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-orange-400 font-mono mt-1">
                              ${price.toLocaleString()}{service.is_sq_ft_pricing ? "/sqft" : "/unit"}
                            </p>
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2">
                            {/* Slider for quantity - wider range for sq ft */}
                            <div className="w-32">
                              <Slider
                                value={[serviceData.quantity]}
                                onValueChange={([val]) => updateQuantity(service.id, val)}
                                max={service.is_sq_ft_pricing ? 50000 : (service.max_quantity || 50)}
                                min={0}
                                step={service.is_sq_ft_pricing ? 100 : 1}
                                className="w-full"
                              />
                            </div>
                            
                            {/* Quantity input - wider for sq ft values */}
                            <Input
                              type="number"
                              min="0"
                              max={service.is_sq_ft_pricing ? 100000 : (service.max_quantity || 50)}
                              value={serviceData.quantity}
                              onChange={(e) => updateQuantity(service.id, parseInt(e.target.value) || 0)}
                              className={cn(
                                "h-8 text-center bg-zinc-950 border-zinc-700 text-zinc-50 text-sm",
                                service.is_sq_ft_pricing ? "w-24" : "w-16"
                              )}
                              placeholder={service.is_sq_ft_pricing ? "sq ft" : "qty"}
                            />
                            
                            {/* Line total */}
                            <div className="w-24 text-right">
                              <span className={cn(
                                "font-mono text-sm",
                                isSelected ? "text-zinc-50" : "text-zinc-600"
                              )}>
                                ${(price * serviceData.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Discount section - shown when item is selected */}
                        {isSelected && (
                          <div className="mt-3 pt-3 border-t border-zinc-700/50 space-y-2">
                            <div className="flex items-center gap-3">
                              <Label className="text-xs text-zinc-400 w-20">Discount:</Label>
                              
                              {/* Mode toggle button */}
                              <button
                                onClick={() => toggleDiscountMode(service.id)}
                                className={cn(
                                  "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
                                  (serviceData.discountMode || 'percent') === 'percent'
                                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                    : "bg-green-500/20 text-green-400 border border-green-500/30"
                                )}
                                title="Toggle between % and $ discount"
                              >
                                {(serviceData.discountMode || 'percent') === 'percent' ? (
                                  <>
                                    <Percent className="h-3 w-3" />
                                    %
                                  </>
                                ) : (
                                  <>
                                    <DollarSign className="h-3 w-3" />
                                    $
                                  </>
                                )}
                              </button>
                              
                              {/* Percent mode inputs */}
                              {(serviceData.discountMode || 'percent') === 'percent' && (
                                <div className="flex items-center gap-2">
                                  <Slider
                                    value={[serviceData.discount || 0]}
                                    onValueChange={([val]) => updateDiscount(service.id, val)}
                                    max={50}
                                    min={0}
                                    step={1}
                                    className="w-24"
                                  />
                                  <Input
                                    type="number"
                                    min="0"
                                    max="50"
                                    value={serviceData.discount || 0}
                                    onChange={(e) => updateDiscount(service.id, parseInt(e.target.value) || 0)}
                                    className="w-14 h-7 text-center bg-zinc-950 border-zinc-700 text-zinc-50 text-xs"
                                  />
                                  <span className="text-xs text-zinc-500">%</span>
                                </div>
                              )}
                              
                              {/* Fixed amount mode inputs */}
                              {serviceData.discountMode === 'fixed' && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-zinc-500">$</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    max={price * serviceData.quantity}
                                    value={serviceData.discountAmount || 0}
                                    onChange={(e) => updateDiscountAmount(service.id, parseFloat(e.target.value) || 0)}
                                    className="w-24 h-7 text-center bg-zinc-950 border-zinc-700 text-zinc-50 text-xs"
                                    placeholder="Amount"
                                  />
                                  {serviceData.discountAmount > 0 && (
                                    <span className="text-xs text-zinc-500">
                                      ({price * serviceData.quantity > 0 
                                        ? Math.round((serviceData.discountAmount / (price * serviceData.quantity)) * 100) 
                                        : 0}%)
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Show calculated discount */}
                              {((serviceData.discount || 0) > 0 || (serviceData.discountAmount || 0) > 0) && (
                                <span className="text-xs text-green-400 font-mono">
                                  -${(serviceData.discountMode === 'fixed' 
                                    ? serviceData.discountAmount 
                                    : (price * serviceData.quantity * serviceData.discount) / 100
                                  ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              )}
                            </div>
                            
                            {/* Discount reason - required if discount > 0 */}
                            {((serviceData.discount || 0) > 0 || (serviceData.discountAmount || 0) > 0) && (
                              <div className="flex items-start gap-3">
                                <Label className={cn(
                                  "text-xs w-20 pt-2",
                                  !serviceData.discountReason?.trim() ? "text-red-400" : "text-zinc-400"
                                )}>
                                  Reason: *
                                </Label>
                                <div className="flex-1">
                                  <Input
                                    value={serviceData.discountReason || ""}
                                    onChange={(e) => updateDiscountReason(service.id, e.target.value)}
                                    placeholder="Why are you requesting this discount?"
                                    className={cn(
                                      "h-8 bg-zinc-950 text-zinc-50 text-xs",
                                      !serviceData.discountReason?.trim()
                                        ? "border-red-500/50 focus:border-red-400" 
                                        : "border-zinc-700"
                                    )}
                                  />
                                  {!serviceData.discountReason?.trim() && (
                                    <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Discount reason is required
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* VEXOS Components Section */}
            <div className="mt-4 border border-zinc-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowVexos(!showVexos)}
                className="w-full px-4 py-3 bg-zinc-800/50 flex items-center justify-between hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-orange-400" />
                  <span className="text-sm font-medium text-zinc-50">VEXOS Components</span>
                  {calculateVexosTotal.total > 0 && (
                    <Badge className="ml-2 text-[10px] bg-orange-500/20 text-orange-400 border-orange-500/30">
                      ${calculateVexosTotal.total.toLocaleString()}
                    </Badge>
                  )}
                </div>
                <span className="text-zinc-400 text-xs">{showVexos ? '▼' : '▶'}</span>
              </button>
              
              {showVexos && (
                <div className="p-4 space-y-4 bg-zinc-900/50">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Base Fee - checkbox toggle */}
                    <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={vexosData.base_fee_qty === 1}
                          onChange={(e) => setVexosData({
                            ...vexosData,
                            base_fee_qty: e.target.checked ? 1 : 0
                          })}
                          className="rounded bg-zinc-800 border-zinc-700"
                        />
                        <span className="text-xs text-zinc-50">Base Fee (${vexosFees.base_fee})</span>
                      </label>
                      {vexosData.base_fee_qty === 1 && (
                        <span className="text-sm text-orange-400 font-mono">${vexosFees.base_fee.toLocaleString()}</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Orbit Fee (${vexosFees.orbit_fee})</Label>
                      <Input
                        type="number"
                        min="0"
                        value={vexosData.orbit_fee_qty}
                        onChange={(e) => setVexosData({ 
                          ...vexosData, 
                          orbit_fee_qty: parseInt(e.target.value) || 0 
                        })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Apartments (${vexosFees.unit_fee_apartments}/unit)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={vexosData.unit_apartments_qty}
                        onChange={(e) => setVexosData({ 
                          ...vexosData, 
                          unit_apartments_qty: parseInt(e.target.value) || 0 
                        })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Villas (${vexosFees.unit_fee_villas}/unit)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={vexosData.unit_villas_qty}
                        onChange={(e) => setVexosData({ 
                          ...vexosData, 
                          unit_villas_qty: parseInt(e.target.value) || 0 
                        })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">360° Views (${vexosFees.fee_360})</Label>
                      <Input
                        type="number"
                        min="0"
                        value={vexosData.fee_360_qty}
                        onChange={(e) => setVexosData({ 
                          ...vexosData, 
                          fee_360_qty: parseInt(e.target.value) || 0 
                        })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={vexosData.custom_design}
                        onChange={(e) => setVexosData({
                          ...vexosData,
                          custom_design: e.target.checked
                        })}
                        className="rounded bg-zinc-800 border-zinc-700"
                      />
                      <span className="text-xs text-zinc-300">Custom Design (+${vexosFees.custom_design})</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={vexosData.rush_deadline}
                        onChange={(e) => setVexosData({
                          ...vexosData,
                          rush_deadline: e.target.checked
                        })}
                        className="rounded bg-zinc-800 border-zinc-700"
                      />
                      <span className="text-xs text-zinc-300">Rush Deadline (+${vexosFees.rush_deadline})</span>
                    </label>
                  </div>
                  
                  {/* Orbit Animation Rendering */}
                  <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={vexosData.orbit_animation}
                        onChange={(e) => setVexosData({
                          ...vexosData,
                          orbit_animation: e.target.checked
                        })}
                        className="rounded bg-zinc-800 border-zinc-700"
                      />
                      <div>
                        <span className="text-xs text-zinc-50">Orbit Animation Rendering</span>
                        <span className="text-xs text-orange-400 ml-2">$2500 first + $1000/extra</span>
                      </div>
                    </label>
                    {vexosData.orbit_animation && vexosData.orbit_fee_qty > 0 && (
                      <div className="text-right">
                        <span className="text-xs text-zinc-500">{vexosData.orbit_fee_qty} orbit{vexosData.orbit_fee_qty > 1 ? 's' : ''}</span>
                        <span className="text-sm text-orange-400 font-mono ml-2">
                          ${calculateVexosTotal.orbitAnimationCost?.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Client Invoice Total (for {vexosFees.vexos_commission_percent}% commission)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <Input
                        type="number"
                        min="0"
                        value={vexosData.client_invoice}
                        onChange={(e) => setVexosData({ 
                          ...vexosData, 
                          client_invoice: parseFloat(e.target.value) || 0 
                        })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm pl-7"
                        placeholder="Total invoice to client"
                      />
                    </div>
                  </div>
                  
                  {/* VEXOS Discount */}
                  <div className="p-3 bg-green-900/20 rounded border border-green-800/30">
                    <Label className="text-xs text-green-400 mb-2 block">VEXOS Discount</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(
                          "h-7 text-xs px-2",
                          vexosDiscount.mode === 'percent' ? "bg-green-600 text-white border-green-600" : "bg-zinc-800 border-zinc-700"
                        )}
                        onClick={() => setVexosDiscount({ ...vexosDiscount, mode: 'percent' })}
                      >
                        %
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(
                          "h-7 text-xs px-2",
                          vexosDiscount.mode === 'fixed' ? "bg-green-600 text-white border-green-600" : "bg-zinc-800 border-zinc-700"
                        )}
                        onClick={() => setVexosDiscount({ ...vexosDiscount, mode: 'fixed' })}
                      >
                        $
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max={vexosDiscount.mode === 'percent' ? 100 : undefined}
                        value={vexosDiscount.mode === 'percent' ? vexosDiscount.percent : vexosDiscount.fixed}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          if (vexosDiscount.mode === 'percent') {
                            setVexosDiscount({ ...vexosDiscount, percent: Math.min(100, val) });
                          } else {
                            setVexosDiscount({ ...vexosDiscount, fixed: val });
                          }
                        }}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 h-7 text-sm w-24"
                        placeholder="0"
                      />
                      <span className="text-xs text-zinc-400">
                        {vexosDiscount.mode === 'percent' ? '%' : '$'}
                      </span>
                    </div>
                    {calculateVexosTotal.discountAmount > 0 && (
                      <p className="text-xs text-green-400 mt-2">
                        Discount: -${calculateVexosTotal.discountAmount.toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  {/* VEXOS Total Summary */}
                  {calculateVexosTotal.subtotal > 0 && (
                    <div className="p-2 bg-zinc-900/50 rounded border border-zinc-700">
                      <div className="flex justify-between text-xs">
                        <span className="text-zinc-400">Components:</span>
                        <span className="text-zinc-50 font-mono">${calculateVexosTotal.componentsTotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-zinc-400">Commission ({vexosFees.vexos_commission_percent}%):</span>
                        <span className="text-zinc-50 font-mono">${calculateVexosTotal.commission.toLocaleString()}</span>
                      </div>
                      {calculateVexosTotal.discountAmount > 0 && (
                        <div className="flex justify-between text-xs mt-1">
                          <span className="text-green-400">Discount:</span>
                          <span className="text-green-400 font-mono">-${calculateVexosTotal.discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-700">
                        <span className="text-zinc-300 font-medium">VEXOS Total:</span>
                        <span className="text-orange-400 font-mono font-bold">${calculateVexosTotal.total.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Right Panel - Summary */}
          <div className="w-80 border-l border-zinc-800 bg-zinc-950 flex flex-col">
            <div className="p-4 border-b border-zinc-800">
              <h3 className="text-sm font-semibold text-zinc-50">Summary</h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="bulk-request-summary">
              {summary.items.length === 0 && calculateVexosTotal.total === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-4">
                  Select services to add them to your request
                </p>
              ) : (
                <>
                  {summary.items.map(item => (
                    <div 
                      key={item.id}
                      className="flex items-start justify-between p-2 bg-zinc-800/50 rounded text-xs"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-50 truncate">{item.name}</p>
                        <p className="text-zinc-500">×{item.quantity}</p>
                        {item.discount > 0 && (
                          <Badge className="mt-1 text-[10px] bg-green-500/20 text-green-400 border-green-500/30">
                            -{item.discount}%
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        {item.discount > 0 && (
                          <p className="text-zinc-500 line-through text-[10px]">
                            ${item.originalPrice.toLocaleString()}
                          </p>
                        )}
                        <p className="text-zinc-50 font-mono">
                          ${item.finalPrice.toLocaleString()}
                        </p>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-zinc-500 hover:text-red-400 mt-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* VEXOS in Summary */}
                  {calculateVexosTotal.total > 0 && (
                    <div className="flex items-start justify-between p-2 bg-orange-500/10 border border-orange-500/30 rounded text-xs">
                      <div className="flex-1 min-w-0">
                        <p className="text-orange-400 font-medium">VEXOS Components</p>
                        <p className="text-zinc-500 text-[10px]">
                          Components: ${calculateVexosTotal.componentsTotal.toLocaleString()}
                          {calculateVexosTotal.commission > 0 && ` + Commission: $${calculateVexosTotal.commission.toLocaleString()}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-orange-400 font-mono font-medium">
                          ${calculateVexosTotal.total.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Total */}
            {(summary.items.length > 0 || calculateVexosTotal.total > 0) && (
              <div className="p-4 border-t border-zinc-800 space-y-3">
                {/* Target Total - auto-distribute discount */}
                {summary.items.length > 0 && (
                <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-2">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-purple-400" />
                    <Label className="text-xs text-purple-400 font-medium">Target Total</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      max={summary.totalWithoutDiscount}
                      value={targetTotal}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTargetTotal(val);
                        if (val) {
                          applyTargetTotal(val);
                        }
                      }}
                      placeholder={`Max: ${summary.totalWithoutDiscount.toLocaleString()}`}
                      className="flex-1 h-8 bg-zinc-950 border-zinc-700 text-zinc-50 text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (targetTotal) {
                          applyTargetTotal(targetTotal);
                          toast.success("Discount distributed across all services");
                        }
                      }}
                      disabled={!targetTotal || parseFloat(targetTotal) >= summary.totalWithoutDiscount}
                      className="h-8 px-2 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    >
                      <Calculator className="h-3 w-3 mr-1" />
                      Apply
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-500">
                    Enter desired total to auto-distribute discount proportionally
                  </p>
                </div>
                )}
                
                {summary.totalDiscount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Subtotal:</span>
                    <span className="text-zinc-500 line-through font-mono">
                      ${summary.totalWithoutDiscount.toLocaleString()}
                    </span>
                  </div>
                )}
                {summary.totalDiscount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-green-400">Total Discount:</span>
                    <span className="text-green-400 font-mono">
                      -${summary.totalDiscount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                )}
                
                {/* Services subtotal */}
                {summary.items.length > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Services:</span>
                    <span className="text-zinc-50 font-mono">
                      ${summary.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                )}
                
                {/* VEXOS subtotal */}
                {calculateVexosTotal.total > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-orange-400">VEXOS:</span>
                    <span className="text-orange-400 font-mono">
                      ${calculateVexosTotal.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                  <span className="text-sm text-zinc-50 font-medium">Grand Total:</span>
                  <span className="text-xl text-orange-400 font-bold font-mono">
                    ${grandTotal.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            )}
            
            {/* Global Notes & Deadline */}
            <div className="p-4 border-t border-zinc-800 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Deadline</Label>
                <Input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="h-8 bg-zinc-900 border-zinc-700 text-zinc-50 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Additional Notes</Label>
                <Textarea
                  value={globalNotes}
                  onChange={(e) => setGlobalNotes(e.target.value)}
                  placeholder="Any additional information..."
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 text-xs min-h-[60px]"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between bg-zinc-900">
          <div className="text-xs text-zinc-500">
            {summary.items.length} service(s){calculateVexosTotal.total > 0 ? ' + VEXOS' : ''} selected
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || (summary.items.length === 0 && calculateVexosTotal.total === 0)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="submit-bulk-requests-btn"
            >
              {submitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit {summary.items.length} Request{summary.items.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPriceRequestModal;
